/**
 * Origem de aquisição das vendas (Trilha F, migration 0062): de onde o CONTATO veio
 * (Meta Ads, Instagram, site, indicação...). Não confundir com `origin` (fluxo interno
 * do pedido) nem com `channelKind` (conta do Bling) — ver `channels.ts`.
 *
 * Puro e sem I/O. Regras que as telas e os testes compartilham:
 *  - pedido sem origem NÃO vira "Não identificado": cai no balde `unset` ("Não informada"),
 *    para o relatório mostrar quanto da receita ainda não tem atribuição;
 *  - slug que não está mais na lista (origem desativada/renomeada) continua legível no
 *    histórico, usando o próprio slug como rótulo;
 *  - conversão = vendas / contatos, e só é calculada quando existe contagem de contatos
 *    para a origem (sem contagem é `null`, nunca 0%).
 */
import { isRevenueState } from "./state-machine.js";
import type { PlatformUnifiedOrder } from "../api/types.js";

/** Como a origem foi definida (migration 0062, `acquisition_source`). */
export const ACQUISITION_SOURCES = ["manual", "meta_ad", "site_link", "inherited"] as const;
export type AcquisitionSource = (typeof ACQUISITION_SOURCES)[number];

export const ACQUISITION_SOURCE_LABELS: Record<AcquisitionSource, string> = {
  manual: "Marcada pela equipe",
  meta_ad: "Detectada no anúncio Meta",
  site_link: "Detectada no link do site",
  inherited: "Herdada da conversa"
};

/** Balde de pedidos sem origem registrada. */
export const UNSET_ORIGIN_KEY = "unset";
export const UNSET_ORIGIN_LABEL = "Não informada";

/** Origem de uma opção cadastrada (`sales_origin_options`). */
export interface SalesOriginOption {
  slug: string;
  label: string;
  active: boolean;
  sortOrder: number;
  /** Preenchida pela captura automática; só desativa, não apaga. */
  system: boolean;
}

/** Slugs usados pela captura automática (F7/F8). Existem no seed da 0062. */
export const META_ADS_ORIGIN = "meta_ads";
export const SITE_ORIGIN = "site";

export function acquisitionOriginKey(order: Pick<PlatformUnifiedOrder, "acquisitionOrigin">): string {
  return order.acquisitionOrigin?.trim() || UNSET_ORIGIN_KEY;
}

export function acquisitionOriginLabel(key: string, options: readonly SalesOriginOption[] = []): string {
  if (key === UNSET_ORIGIN_KEY) return UNSET_ORIGIN_LABEL;
  return options.find((option) => option.slug === key)?.label ?? key;
}

export interface AcquisitionBucket {
  key: string;
  label: string;
  /** Vendas = pedidos em estado de receita (mesma regra de `salesChannelBuckets`). */
  sales: number;
  revenueCents: number;
  /** Todos os pedidos da origem, inclusive cancelados/aguardando pagamento. */
  orders: number;
  /** Contatos (conversas) da origem, quando informados; `null` = sem contagem. */
  contacts: number | null;
  /** vendas / contatos, 0–1; `null` quando não há contagem de contatos. */
  conversion: number | null;
  /** Participação na receita do período, 0–1; `null` com receita total zero. */
  revenueShare: number | null;
  salesShare: number | null;
}

export interface AcquisitionOptions {
  /** Cadastro de origens (rótulos); sem ele, o slug serve de rótulo. */
  options?: readonly SalesOriginOption[];
  /** Conversas por origem (`GET .../acquisition/contacts`); chave = slug, `unset` para sem origem. */
  contactsByOrigin?: Readonly<Record<string, number>>;
}

/**
 * Vendas e receita por origem, da maior receita para a menor (empate: mais vendas).
 * Origem sem nenhum pedido nem contato não aparece.
 */
export function acquisitionBuckets(orders: readonly PlatformUnifiedOrder[], config: AcquisitionOptions = {}): AcquisitionBucket[] {
  const byKey = new Map<string, { orders: number; sales: number; revenueCents: number }>();
  for (const order of orders) {
    const key = acquisitionOriginKey(order);
    const acc = byKey.get(key) ?? { orders: 0, sales: 0, revenueCents: 0 };
    acc.orders += 1;
    if (isRevenueState(order.state)) {
      acc.sales += 1;
      acc.revenueCents += order.totalCents ?? 0;
    }
    byKey.set(key, acc);
  }
  for (const key of Object.keys(config.contactsByOrigin ?? {})) {
    if (!byKey.has(key)) byKey.set(key, { orders: 0, sales: 0, revenueCents: 0 });
  }
  const totalRevenue = [...byKey.values()].reduce((sum, acc) => sum + acc.revenueCents, 0);
  const totalSales = [...byKey.values()].reduce((sum, acc) => sum + acc.sales, 0);
  return [...byKey.entries()]
    .map(([key, acc]): AcquisitionBucket => {
      const contacts = config.contactsByOrigin?.[key] ?? null;
      return {
        key,
        label: acquisitionOriginLabel(key, config.options),
        orders: acc.orders,
        sales: acc.sales,
        revenueCents: acc.revenueCents,
        contacts,
        conversion: contacts && contacts > 0 ? acc.sales / contacts : null,
        revenueShare: totalRevenue > 0 ? acc.revenueCents / totalRevenue : null,
        salesShare: totalSales > 0 ? acc.sales / totalSales : null
      };
    })
    .sort((a, b) => b.revenueCents - a.revenueCents || b.sales - a.sales || a.label.localeCompare(b.label, "pt-BR"));
}

export interface StrategyBucket {
  strategy: string;
  sales: number;
  revenueCents: number;
}

/** Campanhas/estratégias informadas, só de vendas de receita. Sem estratégia não entra. */
export function strategyBuckets(orders: readonly PlatformUnifiedOrder[]): StrategyBucket[] {
  const byStrategy = new Map<string, StrategyBucket>();
  for (const order of orders) {
    const strategy = order.acquisitionStrategy?.trim();
    if (!strategy || !isRevenueState(order.state)) continue;
    // Chave sem caixa/acento: "Black Friday" e "black friday" é a mesma estratégia.
    const key = strategy.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
    const bucket = byStrategy.get(key) ?? { strategy, sales: 0, revenueCents: 0 };
    bucket.sales += 1;
    bucket.revenueCents += order.totalCents ?? 0;
    byStrategy.set(key, bucket);
  }
  return [...byStrategy.values()].sort((a, b) => b.revenueCents - a.revenueCents || b.sales - a.sales);
}

export interface LeadingOrigin {
  bySales: AcquisitionBucket | null;
  byRevenue: AcquisitionBucket | null;
  /** Participação da líder por vendas, 0–1. */
  leaderSalesShare: number | null;
}

/**
 * Cards "principal origem" do dashboard. A balde "Não informada" nunca é líder: ela é o
 * que falta atribuir, não um canal. Sem vendas atribuídas, tudo `null` (a tela mostra
 * "Sem dados", não zero).
 */
export function leadingOrigin(buckets: readonly AcquisitionBucket[]): LeadingOrigin {
  const known = buckets.filter((bucket) => bucket.key !== UNSET_ORIGIN_KEY && bucket.sales > 0);
  const bySales = [...known].sort((a, b) => b.sales - a.sales || b.revenueCents - a.revenueCents)[0] ?? null;
  const byRevenue = [...known].sort((a, b) => b.revenueCents - a.revenueCents || b.sales - a.sales)[0] ?? null;
  return { bySales, byRevenue, leaderSalesShare: bySales?.salesShare ?? null };
}

/** Quantas vendas ainda não têm origem registrada (para o aviso "falta atribuir"). */
export function unsetSales(buckets: readonly AcquisitionBucket[]): { sales: number; totalSales: number } {
  const totalSales = buckets.reduce((sum, bucket) => sum + bucket.sales, 0);
  return { sales: buckets.find((bucket) => bucket.key === UNSET_ORIGIN_KEY)?.sales ?? 0, totalSales };
}

const csvCell = (value: string): string => (/[;"\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

/**
 * CSV do comparativo por origem (Excel pt-BR: separador `;`, decimais com vírgula). Conversão
 * sem contagem de contatos sai VAZIA, não "0%": vazio diz "não sei", 0% diria "ninguém comprou".
 */
export function acquisitionCsv(buckets: readonly AcquisitionBucket[]): string {
  const pct = (value: number | null): string => (value === null ? "" : (value * 100).toFixed(1).replace(".", ","));
  const rows = [["Origem", "Contatos", "Pedidos", "Vendas", "Faturamento (R$)", "Conversão (%)", "Participação no faturamento (%)"]];
  for (const bucket of buckets) {
    rows.push([
      bucket.label,
      bucket.contacts === null ? "" : String(bucket.contacts),
      String(bucket.orders),
      String(bucket.sales),
      (bucket.revenueCents / 100).toFixed(2).replace(".", ","),
      pct(bucket.conversion),
      pct(bucket.revenueShare)
    ]);
  }
  return rows.map((row) => row.map(csvCell).join(";")).join("\r\n");
}
